import {
	billingRefusalDetails,
	domainErrorKind,
	type BillingRefusalDetails,
} from './api-error-kind';

/**
 * How the API client's error interceptor presents a failed request, chosen per call with
 * `meta: { errors }`. Independent of `throwOnError`, which only decides whether the call throws.
 *
 * - `full`: the global full-page error.
 * - `toast`: a warning toast.
 * - `silent`: nothing (logged locally); the caller handles the error.
 * - `signout`: sign out, returning to the current page after logging back in.
 * - `auto` (default): `signout` for a 401, otherwise `toast`.
 *
 * With `throwOnError`, every mode but `signout` throws a SvelteKit `HttpError` (status 500 for a
 * network failure). Aborted requests, redirects, version-policy refusals and refused paid runs are
 * always handled globally and return the error as-is.
 */
export type ApiErrorMode = 'auto' | 'full' | 'toast' | 'silent' | 'signout';

/** Picks a mode from the error and response; `auto` or `undefined` defers to the defaults. */
export type ApiErrorModeFn = (
	error: unknown,
	response: Response | undefined,
) => ApiErrorMode | undefined;

type ApiErrorModeSource = ApiErrorMode | ApiErrorModeFn | undefined;

/**
 * A mode (or function) for every error, or one per response status with an optional `default`.
 * A root mode (or `default`) doesn't apply to a 401, which only a `401` entry overrides.
 */
export type ApiErrorHandling =
	| ApiErrorMode
	| ApiErrorModeFn
	| { [status: number]: ApiErrorModeSource; default?: ApiErrorModeSource };

declare module '$lib/api/core/types.gen' {
	interface ClientMeta {
		errors?: ApiErrorHandling;
	}
}

function modeOf(source: ApiErrorModeSource, error: unknown, response: Response | undefined) {
	const mode = typeof source === 'function' ? source(error, response) : source;
	return mode === 'auto' ? undefined : mode;
}

/** The mode a failed request resolves to, never `auto`. */
export function apiErrorMode(
	handling: ApiErrorHandling | undefined,
	error: unknown,
	response: Response | undefined,
): Exclude<ApiErrorMode, 'auto'> {
	const status = response?.status;
	const root = typeof handling === 'object' ? handling.default : handling;
	const mode =
		(typeof handling === 'object' && status !== undefined
			? modeOf(handling[status], error, response)
			: undefined) ?? (status === 401 ? undefined : modeOf(root, error, response));
	return mode ?? (status === 401 ? 'signout' : 'toast');
}

/**
 * Silence only errors of these domain `kind`s: refusals that are a normal outcome of the call and
 * that the caller words inline. A toast would wrongly call them out as something broken.
 */
export function silentKinds(...kinds: string[]): ApiErrorModeFn {
	return (error) => {
		const kind = domainErrorKind(error);
		return kind !== undefined && kinds.includes(kind) ? 'silent' : undefined;
	};
}

export class UsageUnavailableError extends Error {
	readonly status = 402;
	constructor(
		readonly kind?: string,
		/** For `BILLING_NOT_PROVISIONED`: the payer without a contract, and whether it can set up. */
		readonly details: BillingRefusalDetails = {},
	) {
		super('Usage is unavailable');
	}
}

/** Usage and billing reads: a billing refusal (402) is the caller's, anything else is full-page. */
export const usageErrors = { 402: 'silent', default: 'full' } satisfies ApiErrorHandling;

/** Settle a usage or billing read (without `throwOnError`), throwing a 402 as a `UsageUnavailableError`. */
export async function usageData<T>(
	call: Promise<{ data?: T; error?: unknown; response?: Response }>,
): Promise<NonNullable<T>> {
	const { data, error, response } = await call;
	if (response?.status === 402) {
		throw new UsageUnavailableError(domainErrorKind(error), billingRefusalDetails(error));
	}
	if (error !== undefined) throw error;
	return data as NonNullable<T>;
}
